import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectionService, NotifyService } from '@core/services';
import { PopupComponent } from '@shared/components/popup/popup.component';
import {
  Notifications,
  NotificationsHeading,
  NumberConstants,
  ToastTypes,
} from '../../../configs/app.config';

@Component({
  selector: 'sra-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit, OnChanges {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @Input() editMode!: boolean;
  @Input() notes!: string;
  @Input() urls!: string[];
  @Input() attachmentIds: Array<number | null> = [];
  @Input() syncInProgress = false;
  @Input() editing = false;

  @Output() fileChanged = new EventEmitter<string[]>();
  @Output() fileTypeChanged = new EventEmitter<string[]>();
  @Output() fileNameChanged = new EventEmitter<string[]>();
  @Output() inputChanged = new EventEmitter<string>();
  @Output() editModeChanged = new EventEmitter<boolean>();
  @Output() validationStatus = new EventEmitter<boolean>();

  imageUrls: string[] = [];
  fileTypes: string[] = [];
  fileNames: string[] = [];
  acceptedFileTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  connectionStatus = true;
  numberConstants = NumberConstants;
  showCancelAction = false;
  private wasSyncInProgress = false;

  @ViewChildren('popup') popups!: QueryList<PopupComponent>;

  constructor(
    private readonly notifyService: NotifyService,
    private readonly connectionService: ConnectionService,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit() {
    this.editMode = !(
      this.notes?.length > 1 ||
      (this.urls && this.urls.length > 0)
    );
    this.connectionService
      .getConnectionStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => (this.connectionStatus = status));
  }

  /**
   * Handles updates to component input properties.
   *
   * Applies changes for:
   * - Notes
   * - Edit mode state
   * - Attachment URLs
   * - Attachment IDs
   * - Attachment sync progress
   *
   * Automatically enables edit mode when:
   * - There is no existing content
   * - Notes exceed the maximum allowed length
   *
   * @param {SimpleChanges} changes
   * @memberof AttachmentComponent
   */
  ngOnChanges(changes: SimpleChanges) {
    this.applyNotesChange(changes);
    this.applyEditModeChange(changes);
    this.applyUrlsChange(changes);
    this.applyAttachmentIdsChange(changes);
    this.applySyncInProgressChange(changes);

    if (this.shouldForceEditMode()) {
      setTimeout(() => {
        this.editMode = true;
        this.editModeChanged.emit(this.editMode);
      });
    }
  }

  /**
   * Determines whether edit mode should be automatically enabled.
   *
   * Edit mode is forced when:
   * - No notes and no attachments exist
   * - Notes exceed the maximum allowed length
   *
   * @return {*} {boolean}
   *
   * @memberof AttachmentComponent
   */
  private shouldForceEditMode(): boolean {
    const hasNoContent =
      (!this.notes || this.notes.trim().length === 0) &&
      (!this.urls || this.urls.length === 0);
    const hasInvalidNotes =
      !!this.notes && this.notes.length > this.numberConstants.MAX_NOTES_LENGTH;

    return (!this.editMode && hasNoContent) || hasInvalidNotes;
  }

  /**
   * Applies updates when the notes input changes.
   *
   * @param {SimpleChanges} changes
   * @memberof AttachmentComponent
   */
  private applyNotesChange(changes: SimpleChanges): void {
    if (changes['notes']) {
      this.notes = changes['notes'].currentValue;
    }
  }
  /**   * Applies changes to the editMode input property and updates the showCancelAction state.
   */
  private applyEditModeChange(changes: SimpleChanges): void {
    if (!changes['editMode']) {
      return;
    }
    this.editMode = changes['editMode'].currentValue;
    if (!this.editMode) {
      this.showCancelAction = false;
    }
  }
  /**   * Applies changes to the urls input property and updates the internal imageUrls state.
   */
  private applyUrlsChange(changes: SimpleChanges): void {
    if (!changes['urls']) {
      return;
    }
    const incoming = changes['urls'].currentValue || [];
    const newUrls = Array.isArray(incoming) ? incoming : [incoming];
    this.urls = [...newUrls];
    this.imageUrls = [...newUrls];
  }
  /**   * Applies changes to the attachmentIds input property.
   */
  private applyAttachmentIdsChange(changes: SimpleChanges): void {
    if (!changes['attachmentIds']) {
      return;
    }
    const incomingIds = changes['attachmentIds'].currentValue || [];
    this.attachmentIds = Array.isArray(incomingIds) ? [...incomingIds] : [];
  }

  /**
   * Applies changes to the syncInProgress input property.
   *
   * When sync is in progress, ensure edit mode stays true so loading indicator shows.
   * Only exit edit mode when sync completes successfully and all attachments are saved.
   */
  private applySyncInProgressChange(changes: SimpleChanges): void {
    if (!changes['syncInProgress']) {
      return;
    }
    this.syncInProgress = !!changes['syncInProgress'].currentValue;
    const didUploadJustFinish = this.wasSyncInProgress && !this.syncInProgress;
    this.wasSyncInProgress = this.syncInProgress;

    // Keep edit mode true while sync is in progress to maintain loading indicator
    if (this.syncInProgress) {
      this.editMode = true;
    }

    // Only exit edit mode when upload finishes successfully and all files are saved
    if (
      didUploadJustFinish &&
      this.editMode &&
      !this.showCancelAction &&
      this.hasSavedAttachmentForView() &&
      !this.hasPendingUploads()
    ) {
      this.editMode = false;
      this.editModeChanged.emit(this.editMode);
    }
  }

  /**
   * Check if there are any pending uploads (URLs without corresponding attachmentIds)
   */
  private hasPendingUploads(): boolean {
    return this.imageUrls.some(
      (imageUrl, index) => imageUrl && !this.attachmentIds[index],
    );
  }

  /**
   * Opens the native file browser so the user can pick image attachments.
   */
  openFileBrowser(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Handles file selection from the native file dialog and validates images.
   * Emits updated URLs, types and names for accepted files.
   */
  onFilesSelected(event: Event): void {
    const inputEvent = event.target as HTMLInputElement;
    if (!inputEvent.files || inputEvent.files.length === 0) return;

    const allSelectedFiles = Array.from(inputEvent.files);
    if (
      allSelectedFiles.length > 2 ||
      allSelectedFiles.length + this.imageUrls.length > 2
    ) {
      this.notifyService.show(
        ToastTypes.Warning,
        NotificationsHeading.WARNING,
        Notifications.FILE_LIMIT_EXCEEDED,
      );
      inputEvent.value = '';
      return;
    }
    const selectedFiles = allSelectedFiles.slice(0, 2);

    this.alignMetadataWithImages();
    let filesProcessed = 0;
    selectedFiles.forEach((file) => {
      const fileSizeInMb = file.size / NumberConstants.FILE_SIZE_CALCULATION;
      const fileType = file.type;

      if (fileSizeInMb > NumberConstants.MAX_FILE_SIZE) {
        this.notifyService.show(
          ToastTypes.Error,
          NotificationsHeading.ERROR,
          Notifications.FILE_SIZE_EXCEEDED,
        );
        filesProcessed++;
        return;
      }

      if (!this.acceptedFileTypes.includes(fileType)) {
        this.notifyService.show(
          ToastTypes.Error,
          NotificationsHeading.ERROR,
          Notifications.FILE_TYPE_DIFFERENT,
        );
        filesProcessed++;
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.imageUrls.push(reader.result as string);
        this.fileTypes.push(fileType);
        this.fileNames.push(file.name);
        filesProcessed++;

        if (filesProcessed === selectedFiles.length) {
          this.autoSave();
        }
      };
      reader.readAsDataURL(file);
    });
    inputEvent.value = '';
  }
  /**
   * Removes a selected image from the upload list and triggers an autosave update.
   */
  removeImage(index: number): void {
    URL.revokeObjectURL(this.imageUrls[index]);
    this.imageUrls.splice(index, 1);
    this.fileTypes.splice(index, 1);
    this.fileNames.splice(index, 1);
    this.fileInput.nativeElement.value = '';
    this.autoSave();
  }

  /**
   * Emits text input changes from the notes field and saves the current attachment state.
   */
  handleUserInput(input: string) {
    this.inputChanged.emit(input);
    this.autoSave();
  }

  /**
   * Emits the current validation state for the notes and attachments form.
   */
  handleValidation(isValid: boolean) {
    this.validationStatus.emit(isValid);
  }

  /**
   * Checks whether the current file selection differs from the saved attachment URLs.
   */
  hasChanges(): boolean {
    if (this.imageUrls.length !== this.urls.length) {
      return true;
    }
    const sortedImageUrls = [...this.imageUrls].sort();
    const sortedUrls = [...this.urls].sort();
    return !sortedImageUrls.every((url, index) => url === sortedUrls[index]);
  }

  /**
   * Emits current file metadata and updates the internal URL state after any change.
   */
  private autoSave(): void {
    this.fileTypeChanged.emit([...this.fileTypes]);
    this.fileNameChanged.emit([...this.fileNames]);
    this.fileChanged.emit([...this.imageUrls]);
    this.urls = [...this.imageUrls];
  }

  /**
   * Ensures metadata arrays remain aligned with the current list of selected images.
   */
  private alignMetadataWithImages(): void {
    while (this.fileTypes.length < this.imageUrls.length) {
      this.fileTypes.push('');
    }
    while (this.fileNames.length < this.imageUrls.length) {
      this.fileNames.push('');
    }
  }

  /**
   * Toggles between edit and view mode for the file upload UI.
   */
  toggleEditMode() {
    this.imageUrls = [...this.urls];
    this.fileTypes = [];
    this.fileNames = [];
    this.editMode = !this.editMode;
    this.showCancelAction = this.editMode;
    this.editModeChanged.emit(this.editMode);
  }

  /**
   * Cancels the current edit operation and restores the saved attachment state.
   */
  handleCancel() {
    this.imageUrls = [...this.urls];
    this.fileTypes = [];
    this.fileNames = [];
    this.editMode = false;
    this.showCancelAction = false;
    this.editModeChanged.emit(this.editMode);
  }

  /**
   * Clears all selected files and resets the component to the edit state.
   */
  handleDelete() {
    this.urls = [];
    this.notes = '';
    this.imageUrls = [];
    this.fileTypes = [];
    this.fileNames = [];
    this.inputChanged.emit(this.notes);
    this.fileTypeChanged.emit(this.fileTypes);
    this.fileNameChanged.emit(this.fileNames);
    this.fileChanged.emit(this.urls);
    this.editMode = true;
    this.editModeChanged.emit(this.editMode);
  }

  /**
   * Determines whether the delete control should be shown for an attachment.
   */
  canShowDelete(index: number): boolean {
    return this.editMode && Boolean(this.attachmentIds[index]);
  }

  /**
   * Returns true when the attachment preview should display a loading indicator.
   */
  showImageLoader(index: number): boolean {
    return (
      this.editMode && !!this.imageUrls[index] && !this.attachmentIds[index]
    );
  }

  /**
   * Checks whether any currently visible attachments have already been saved to the server.
   */
  private hasSavedAttachmentForView(): boolean {
    return (
      this.imageUrls.length > 0 &&
      this.attachmentIds.some((attachmentId) => !!attachmentId)
    );
  }

  /**
   * Opens the popup modal for the selected image preview.
   */
  openPopup(index: number): void {
    const popup = this.popups.get(index);
    if (popup) popup.openPopup();
  }
}
